import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/api/ApiContext';
import type { AccountCreateRequest } from '@/api/handlers/resources/accounts/requests';
import type { AccountWriteResponse } from '@/api/handlers/resources/accounts/responses';
import type { ApiResponse } from '@/api/handlers/responses';
import { AccountQueryKeys } from './accountQueryKeys';

/**
 * Create an account.
 *
 * Idempotent on the client-supplied `id`, so a retried submit — a double click,
 * a resend after a dropped connection — returns the account that was already
 * created instead of a second one. The server answers `201` the first time and
 * `200` on the replay; `makeApiRequest` treats both as success, so the caller
 * never has to tell them apart.
 *
 * Invalidates the whole accounts key (every locale's list, every cached detail)
 * so the new row, its opening balance, and its parent's now-header status all
 * show.
 *
 * Never rejects: callers branch on `result.success` and read `result.error.code`.
 */
export const useCreateAccountMutation = () => {
    const { apiClient } = useApi();
    const queryClient = useQueryClient();

    return useMutation<
        ApiResponse<AccountWriteResponse>,
        Error,
        AccountCreateRequest
    >({
        mutationFn: (body) => apiClient.accounts.createAccount(body),
        onSuccess: (result) => {
            if (result.success) {
                queryClient.invalidateQueries({
                    queryKey: AccountQueryKeys.all,
                });
            }
        },
    });
};
